/**
 * wms-forecast-client — API principale
 *
 * Usage :
 *   import { fetchForecast } from 'wms-forecast-client';
 *
 *   const { data, abort } = fetchForecast({
 *     model:      'HRDPS',
 *     variables:  ['TT', 'ES'],
 *     lat:        45.5,
 *     lon:        -73.6,
 *     hours:      48,
 *     deltaHours: 1,
 *     dateo:      null,        // null = dernière passe disponible
 *     onProgress: (pct) => {} // 0.0 → 1.0
 *   });
 *
 *   const result = await data;
 *   // result.hhh        → [0, 1, 2, ...]
 *   // result.dateo      → Date
 *   // result.grid_point → { lon, lat }
 *   // result.TT         → [val0, val1, ...]
 *   // result.ES         → [val0, val1, ...]
 */

import { layerNames, metaLayer, styleForVariable } from './layers.js';
import { fetchCapabilities } from './capabilities.js';
import { fetchFeatureInfo, buildTimeSteps } from './wms.js';
export { IMETEO_UV, WMS_MS_TO_KT }          from './transforms.js';
export { productConfig, supportedProducts } from './products.js';

/**
 * @typedef {object} ForecastOptions
 * @property {string}   model       - 'HRDPS' | 'RDPS' | 'GDPS' | 'RAQDPS' | ...
 * @property {string[]} variables   - codes WMS ex: ['TT', 'ES']
 * @property {number}   lat
 * @property {number}   lon
 * @property {number}   [hours=48]          - horizon total en heures
 * @property {number}   [deltaHours=1]      - pas de temps uniforme en heures (ignoré si stepSchedule est fourni)
 * @property {Array<{until?: number, step: number}>} [stepSchedule]
 *   Intervalles variables. Ex: [{until:12, step:1}, {step:3}] → toutes les heures
 *   jusqu'à H+12, puis toutes les 3h. Remplace deltaHours si fourni.
 * @property {boolean}  [fromNow=false]     - si true, ignore les heures passées (< heure courante)
 * @property {Date|null} [dateo=null]       - null = dernière passe disponible
 * @property {function} [onProgress]        - callback(pct: number) 0.0 → 1.0
 *
 * @typedef {object} ForecastResult
 * @property {Date}     dateo       - origine de la prévision
 * @property {number[]} hhh         - heures écoulées depuis dateo
 * @property {{lon:number, lat:number}} grid_point - point de grille retourné par le WMS
 * @property {Object.<string, (number|null)[]>} [variable] - une entrée par variable demandée
 */

/**
 * @typedef {object} ForecastMeta
 * @property {Date}     dateo       - origine de la prévision (dernière passe si dateo=null)
 * @property {number}   deltaHours  - pas de temps du modèle en heures
 * @property {{lon:number, lat:number}} grid_point - point de grille le plus proche
 */

/**
 * Récupère uniquement les métadonnées d'une prévision : dateo, grid_point et deltaHours.
 * Fait seulement 2 requêtes WMS (GetCapabilities + GetFeatureInfo P0).
 * Utile pour vérifier un cache avant de lancer fetchForecast.
 *
 * @param {{ model: string, lat: number, lon: number, dateo?: Date|null }} options
 * @returns {{ data: Promise<ForecastMeta>, abort: function }}
 */
export function fetchForecastMeta(options) {
  const controller = new AbortController();
  const data = _fetchForecastMeta(options, controller.signal);
  return { data, abort: () => controller.abort() };
}

async function _fetchForecastMeta({ model, lat, lon, dateo = null }, signal) {
  const ttLayer = metaLayer(model);

  // 1. Capabilities → dateo réel + deltaHours
  const caps = await fetchCapabilities(ttLayer, signal);
  const refTime = dateo ?? caps.dateo;
  const refTimeStr = _isoZ(refTime);

  // 2. GetFeatureInfo à P0 → point de grille
  const { gridLon, gridLat } = await fetchFeatureInfo(
    { layer: ttLayer, lat, lon, time: refTimeStr, refTime: refTimeStr },
    signal
  );

  return {
    dateo:      refTime,
    deltaHours: caps.deltaHours,
    grid_point: { lon: gridLon, lat: gridLat },
  };
}

/**
 * Lance le téléchargement d'une série temporelle WMS.
 *
 * @param {ForecastOptions} options
 * @returns {{ data: Promise<ForecastResult>, abort: function }}
 */
export function fetchForecast(options) {
  const controller = new AbortController();
  const data = _fetchForecast(options, controller.signal);
  return { data, abort: () => controller.abort() };
}

async function _fetchForecast(options, signal) {
  const {
    model,
    variables,
    lat,
    lon,
    hours        = 48,
    deltaHours   = 1,
    stepSchedule = null,
    fromNow      = false,
    dateo        = null,
    onProgress   = null,
    transforms   = [],
  } = options;

  // 1. Nom des couches WMS
  const layers = layerNames(model, variables);

  // 2. Capabilities → dateo réel si non fourni
  // On utilise toujours TT pour le DIM_REFERENCE_TIME car les variables accumulées
  // (PR, SN, RN...) ont un dateo décalé d'une heure dans les capabilities WMS.
  const ttLayer = metaLayer(model);
  const caps = await fetchCapabilities(ttLayer, signal);
  const refTime = dateo ?? caps.dateo;

  // 3. Construire la liste des instants à récupérer
  // Intervalles : schedule variable si fourni, sinon pas uniforme (au moins caps.deltaHours)
  const effectiveSteps = stepSchedule
    ? stepSchedule.map(s => ({ ...s, step: Math.max(s.step, caps.deltaHours) }))
    : Math.max(deltaHours, caps.deltaHours);

  // Heure de départ : si fromNow, sauter les heures déjà passées
  const startHour = fromNow
    ? Math.max(0, Math.floor((Date.now() - refTime.getTime()) / 3600000))
    : 0;

  const { times, hhh } = buildTimeSteps(refTime, hours, effectiveSteps, startHour);
  const refTimeStr = _isoZ(refTime);

  // 4. Lancer les requêtes avec concurrence limitée pour éviter les HTTP 429
  const CONCURRENCY = 10;
  const total = times.length * layers.length;
  let completed = 0;

  // ff[iTime][iVar] = valeur numérique
  const ff = Array.from({ length: times.length }, () => new Array(layers.length).fill(null));
  // Point de grille (on prend le premier résultat non-null)
  let gridPoint = { lon: null, lat: null };

  // Construire la liste de toutes les tâches
  const tasks = [];
  for (let iTime = 0; iTime < times.length; iTime++) {
    for (let iVar = 0; iVar < layers.length; iVar++) {
      tasks.push({ iTime, iVar });
    }
  }

  // Exécuter avec un pool de CONCURRENCY workers
  async function worker(queue) {
    while (queue.length > 0) {
      const { iTime, iVar } = queue.shift();
      const { value, gridLon, gridLat } = await fetchFeatureInfo(
        { layer: layers[iVar], lat, lon, time: times[iTime], refTime: refTimeStr, style: styleForVariable(variables[iVar]) },
        signal
      );
      ff[iTime][iVar] = value;
      if (gridPoint.lon === null && gridLon !== null) {
        gridPoint = { lon: gridLon, lat: gridLat };
      }
      completed++;
      if (onProgress) onProgress(completed / total);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, tasks.length) }, () => worker(tasks))
  );

  // 5. Assembler le résultat : une entrée par variable
  const result = {
    dateo:      refTime,
    hhh,
    grid_point: gridPoint,
  };
  for (let iVar = 0; iVar < variables.length; iVar++) {
    result[variables[iVar]] = ff.map(row => row[iVar]);
  }

  // 6. Appliquer les transforms
  for (const t of transforms) {
    const inputIndices = t.inputs.map(v => variables.indexOf(v));
    result[t.output] = ff.map(row => t.fn(...inputIndices.map(i => row[i])));
  }

  return result;
}

/** @param {Date} d @returns {string} */
function _isoZ(d) {
  return d.toISOString().slice(0, 19) + 'Z';
}
